/**
 * Competency sign-offs on the Records tab (plan docs/plans/lms-plan.md 7.4;
 * R18; WP-41). A clinical skills course is complete when the quiz is passed
 * AND a qualified observer has signed the checklist off (7.4). The learner
 * asks for the sign-off from the player's Done stage or from here; the list
 * shows each request with its observer ("any admin" when none is named),
 * its state, and the latest observation ("Observed on 12 Sep: not yet, see
 * notes", 7.4).
 *
 * The request names no observer: Appendix A gives a learner no list of the
 * site's lms admins to pick from, and "any admin" is allowed (7.4); every
 * lms admin at the learner's sites is told (9.2 signoff_requested). One open
 * request per person per course (5.5): a second is 409, shown in words.
 */
import { useEffect, useState } from 'react';
import { ClipboardCheck, Send } from 'lucide-react';
import Modal from '../../../ui/Modal';
import { fieldCls, outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { apiErrorCode, apiErrorMessage } from '../../api/client';
import { requestSignoff } from '../../api/completions';
import { listCourses } from '../../api/courses';
import type { CourseListItem } from '../../api/courses';
import { listMySignoffRequests } from '../../api/records';
import type { MySignoffRequest } from '../../api/records';
import type { LmsList } from '../../api/client';
import { formatDay } from '../../components/DueBadge';
import { Pill } from '../../components/StatusPill';
import type { PillTone } from '../../components/StatusPill';
import { Section, SectionError, SectionSkeleton } from '../home/Section';
import { useLoad } from '../home/useLoad';

const REQUEST_PILL: Readonly<Record<MySignoffRequest['status'], { tone: PillTone; word: string }>> = {
  open: { tone: 'amber', word: 'Waiting for observation' },
  done: { tone: 'green', word: 'Signed off' },
  cancelled: { tone: 'gray', word: 'Cancelled' },
};

/** 7.4's line for the latest observation. */
export function observationLine(req: MySignoffRequest): string | null {
  const s = req.last_signoff;
  if (!s) return null;
  const day = formatDay(s.observed_at);
  return s.outcome === 'competent' ? `Observed on ${day}: competent` : `Observed on ${day}: not yet, see notes`;
}

function RequestSignoffDialog({ isOpen, onClose, onSent }: { isOpen: boolean; onClose: () => void; onSent: () => void }) {
  const { toast } = useToast();
  const [courses, setCourses] = useState<CourseListItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [courseId, setCourseId] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setCourses(null);
    setLoadError(null);
    setCourseId('');
    setProblem(null);
    listCourses({ limit: 200 }).then(
      (page) => {
        if (cancelled) return;
        const items = Array.isArray(page?.items) ? page.items : [];
        setCourses(items.filter((c) => c.kind === 'competency' && c.is_active !== false).sort((a, b) => a.title.localeCompare(b.title)));
      },
      (err: unknown) => {
        if (!cancelled) {
          setCourses([]);
          setLoadError(apiErrorMessage(err, 'The course list could not be loaded.'));
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const send = async () => {
    if (sending) return;
    if (!courseId) {
      setProblem('Choose the course to be observed on.');
      return;
    }
    setSending(true);
    setProblem(null);
    try {
      await requestSignoff(courseId);
      toast('Sign-off requested. A Learning Center admin will observe you and record it.', { type: 'success' });
      onSent();
      onClose();
    } catch (err) {
      setProblem(
        apiErrorCode(err) === 'LMS_CONFLICT'
          ? 'A sign-off request for this course is already open.'
          : apiErrorMessage(err, 'The sign-off request could not be sent. Try again.')
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => (sending ? undefined : onClose())} title="Request a competency sign-off" maxWidth="max-w-md">
      <div className="space-y-4" data-testid="lms-signoff-dialog">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Any Learning Center admin at your site can observe you perform the listed steps and sign them off.
        </p>
        <div>
          <label htmlFor="lms-signoff-course" className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            Course
          </label>
          <select
            id="lms-signoff-course"
            className={`${fieldCls} mt-1 w-full`}
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            disabled={courses === null || courses.length === 0}
            data-testid="lms-signoff-course"
          >
            <option value="">{courses === null ? 'Loading courses...' : courses.length === 0 ? 'No clinical skills course needs a sign-off' : 'Choose a course'}</option>
            {(courses ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
          {loadError ? <p className="mt-1 text-xs text-red-700 dark:text-red-400">{loadError}</p> : null}
        </div>
        <div aria-live="polite">
          {problem ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300" data-testid="lms-signoff-problem">
              {problem}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button type="button" className={primaryBtnCls} onClick={() => void send()} disabled={sending} data-testid="lms-signoff-send">
            <Send className="h-4 w-4" aria-hidden="true" />
            {sending ? 'Sending...' : 'Request sign-off'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function SignoffsSection({ reloadKey }: { reloadKey: number }) {
  const requests = useLoad<LmsList<MySignoffRequest>>(
    () => listMySignoffRequests({ limit: 200 }),
    'Your sign-off requests could not be loaded.',
    reloadKey
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const rows = requests.data?.items ?? [];

  return (
    <Section
      title="Competency sign-offs"
      icon={ClipboardCheck}
      count={requests.data ? rows.length : null}
      headingId="lms-records-signoffs-title"
      data-testid="lms-signoffs"
      actions={
        <button type="button" className={outlineBtnCls()} onClick={() => setDialogOpen(true)} data-testid="lms-signoff-open">
          <Send className="h-4 w-4" aria-hidden="true" />
          Request a sign-off
        </button>
      }
    >
      {requests.loading ? (
        <SectionSkeleton rows={1} />
      ) : requests.error ? (
        <SectionError message={requests.error} onRetry={requests.reload} />
      ) : rows.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400" data-testid="lms-signoffs-empty">
          No sign-off requests. A clinical skills course is complete once you pass its quiz and an observer signs your
          skills off.
        </p>
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700" data-testid="lms-signoff-list">
          {rows.map((r) => {
            const pill = REQUEST_PILL[r.status] ?? { tone: 'gray' as PillTone, word: r.status };
            const line = observationLine(r);
            const observer = r.observer ? r.observer.full_name || r.observer.email || 'Named observer' : 'Any admin';
            return (
              <li key={r.id} className="flex flex-col gap-1 px-4 py-3" data-testid="lms-signoff-row">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.course?.title ?? 'Course'}</span>
                  <Pill tone={pill.tone}>{pill.word}</Pill>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{`Requested ${formatDay(r.requested_at)}, observer: ${observer}`}</p>
                {line ? (
                  <p className="text-xs text-gray-700 dark:text-gray-300" data-testid="lms-signoff-observation">
                    {line}
                    {r.last_signoff?.notes ? `. ${r.last_signoff.notes}` : ''}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      <RequestSignoffDialog isOpen={dialogOpen} onClose={() => setDialogOpen(false)} onSent={() => requests.reload()} />
    </Section>
  );
}

export default SignoffsSection;
